/**
 * useLocations Hook
 *
 * Fetches farm locations/rooms from the database for inspection rounds
 * and container management.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Hook to fetch active locations from the database
 * @param {Object} options - Filter options
 * @param {string} [options.locationType] - Filter by location type ('room', 'area', 'zone', 'equipment')
 * @param {boolean} [options.activeOnly=true] - Only fetch active locations
 * @returns {Object} { locations, loading, error, refetch }
 */
export function useLocations(options = {}) {
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { locationType, activeOnly = true } = options

  const fetchLocations = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('locations')
        .select('*')
        .order('sort_order', { ascending: true })

      if (activeOnly) {
        query = query.eq('is_active', true)
      }

      if (locationType) {
        query = query.eq('location_type', locationType)
      }

      const { data, error: queryError } = await query

      if (queryError) {
        throw queryError
      }

      // Transform to match the format expected by InspectionRounds
      const transformed = (data || []).map(loc => ({
        id: loc.code,  // Use code as id for backwards compatibility
        name: loc.name,
        expectedStatus: loc.expected_status,
        locationType: loc.location_type,
        capacity: loc.capacity,
        description: loc.description,
        // Keep original data for reference
        _raw: loc
      }))

      setLocations(transformed)
    } catch (err) {
      console.error('[useLocations] Error:', err)
      setError(err.message)
      setLocations([])
    } finally {
      setLoading(false)
    }
  }, [locationType, activeOnly])

  useEffect(() => {
    fetchLocations()
  }, [fetchLocations])

  return {
    locations,
    loading,
    error,
    refetch: fetchLocations
  }
}

/**
 * Hook to fetch rooms specifically (location_type = 'room')
 * Convenience wrapper for inspection rounds
 * @returns {Object} { rooms, loading, error, refetch }
 */
export function useRooms() {
  const { locations, loading, error, refetch } = useLocations({ locationType: 'room' })

  return {
    rooms: locations,
    loading,
    error,
    refetch
  }
}

export default useLocations
